// Camera-recording flow — #297 PR C.
//
// Mirrors `recording-flow.ts` but uses `startCameraRecording` so the
// recorded blob is a canvas composition of screen + camera. Reuses the
// same consent modal + recording overlay UI (exported from
// recording-flow.ts) — only the recording client + the consent disclosure
// bullet differ.
//
// Bundle hygiene: dynamic-imported from ui.ts only when the user clicks
// the "Record screen with camera" button, so the canvas composition code
// + the additional disclosure don't ship in the always-loaded widget
// bundle.
//
// Compliance ordering: the consent modal must be accepted BEFORE
// getDisplayMedia OR getUserMedia is called. Same enforcement as the
// existing screen-only flow.

import { en } from './i18n/en.ts';
import type { SupportedLocale, WidgetMessages } from './i18n/index.ts';
import { showConsentModal, showRecordingOverlay } from './recording-flow.ts';
import {
  type CameraRecordingResult,
  startCameraRecording as defaultStartCameraRecording,
} from './screen-recording-client.ts';

export interface CameraRecordingFlowOptions {
  /** ShadowRoot the widget renders into. Modal + overlay get appended here. */
  shadow: ShadowRoot;
  /** Workspace display name shown in the consent disclosure. */
  workspaceName: string;
  /**
   * Retention period in days, shown in the disclosure copy. Caller passes
   * the workspace's current `attachment_retention_days` so the modal text
   * matches reality (default 90 if not yet customized).
   */
  retentionDays: number;
  /** Pluggable for tests. Defaults to screen-recording-client.startCameraRecording. */
  startCameraRecording?: typeof defaultStartCameraRecording;
  /**
   * Best-effort callback fired at the four lifecycle moments. Caller wires
   * this to api.submitWidgetEvent so the events land in auth_events. The
   * `camera_denied_fallback` event marks the partial-fulfillment path
   * separately from a clean recording so the workspace owner can spot
   * trends in customer denials.
   */
  onAuditEvent?: (event: {
    type:
      | 'recording_consent_accepted'
      | 'recording_started'
      | 'recording_stopped'
      | 'camera_denied_fallback';
    metadata?: Record<string, unknown>;
  }) => void;
  messages?: WidgetMessages;
  locale?: SupportedLocale;
  /**
   * Set when the caller already showed the consent modal (with its "Include
   * camera" box ticked) and already fired `recording_consent_accepted`.
   * That is the normal path now: the single "Record screen" button collects
   * consent once, then routes here or to the screen-only flow based on the
   * reporter's choice. Consent still precedes getDisplayMedia/getUserMedia.
   */
  skipConsent?: boolean;
}

/**
 * Run the full camera-recording flow start-to-finish. Returns the final
 * CameraRecordingResult (blob + metadata, including `cameraCaptured` so the
 * caller can render a "camera unavailable" notice on the partial-fulfillment
 * path) on success, or null if the user cancels at any step (consent
 * decline, screen-pick dismiss, manual cancel during recording).
 */
export async function runCameraRecordingFlow(
  opts: CameraRecordingFlowOptions,
): Promise<CameraRecordingResult | null> {
  if (!opts.skipConsent) {
    const consent = await showConsentModal({
      shadow: opts.shadow,
      workspaceName: opts.workspaceName,
      retentionDays: opts.retentionDays,
      messages: opts.messages,
      locale: opts.locale,
      cameraDisclosure: true,
    });
    if (!consent.accepted) return null;
    opts.onAuditEvent?.({ type: 'recording_consent_accepted' });
  }

  const startFn = opts.startCameraRecording ?? defaultStartCameraRecording;
  let controller: Awaited<ReturnType<typeof defaultStartCameraRecording>>;
  try {
    controller = await startFn({});
  } catch {
    // Display-pick dismissed, support check failed mid-call, etc.
    return null;
  }
  opts.onAuditEvent?.({ type: 'recording_started' });

  const userStopped = await showRecordingOverlay(
    opts.shadow,
    controller,
    opts.messages ?? en,
    opts.locale,
  );
  if (!userStopped) {
    controller.cancel();
    opts.onAuditEvent?.({
      type: 'recording_stopped',
      metadata: { resolution: 'cancelled' },
    });
    return null;
  }
  const result = await controller.stop();

  // Separate audit event for the camera-denied partial-fulfillment path so
  // the workspace owner can spot trends without parsing the resolution
  // metadata. The screen-only `recording_stopped` event still fires below
  // for parity with the existing flow's audit shape.
  if (!result.cameraCaptured) {
    opts.onAuditEvent?.({
      type: 'camera_denied_fallback',
      metadata: { duration_ms: result.durationMs },
    });
  }

  opts.onAuditEvent?.({
    type: 'recording_stopped',
    metadata: {
      resolution: 'completed',
      duration_ms: result.durationMs,
      auto_stopped: result.autoStopped,
      audio_captured: result.audioCaptured,
      camera_captured: result.cameraCaptured,
    },
  });
  return result;
}
